import React, { useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { setAnswer } from 'reducers/siteVisitReport';
import { openModal } from 'reducers/modals';
// components
import { UnmountClosed } from 'react-collapse';
import { FormattedMessage } from 'react-intl';
import QuestionComments from '../QuestionComments';
import QuestionNewComment from '../QuestionNewComment';
import QuestionAnswerIcon from '../QuestionAnswerIcon';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import SVRBadge from 'components/SVRBadge';
import { ReactComponent as PencilIcon } from './pencil.svg';
import Button from 'components/Button';
// constants
import { answerButtons, answerValues } from './constants';
// utils
import classnames from 'classnames/bind';
import { isMobile } from 'react-device-detect';
import mixpanel from 'mixpanel-browser';
// styles
import styles from './QuestionRow.module.scss';

const cn = classnames.bind(styles);

const QuestionRow = (
  { setAnswer, openModal, question, questionIndex, categoryIndex, currentUser, farmId, reportId, isDraft }
) => {
  const [isNoteOpen, setNoteOpen] = useState(() => isMobile && question.open_answer);
  const answer = question.answer?.value;
  const isCorrected = !!question.answer?.is_corrected;
  const isNotCorrected = !!question.answer?.not_corrected;
  const isCarriedForward = !!question.answer?.carried_forward;
  // Carried-forward questions still failed in a draft only accept a corrected / not corrected decision
  const isCarryForwardDecisionMode =
    isDraft && isCarriedForward && answer === answerValues.fail && !question.open_answer;
  const [openAnswerText, setOpenAnswerText] = useState(answer || '');

  const toggleNote = () => {
    if (answer || question.open_answer) {
      setNoteOpen(!isNoteOpen);
    }
  };

  const trackQuestionAction = (action, extra = {}) => {
    if (currentUser && currentUser.id) {
      mixpanel.track('Feature Used', {
        user_id: currentUser.id,
        feature_name: 'QuestionRow',
        action,
        question_label: question.label,
        question_index: questionIndex,
        category_index: categoryIndex,
        farm_id: farmId,
        report_id: reportId,
        timestamp: new Date().toISOString(),
        ...extra,
      });
    }
  };

  const confirmRemoveCorrected = (handleConfirm) => {
    openModal(
      <ConfirmationModal
        title={<FormattedMessage id="general.removeCorrectedStatus" />}
        actionBtnLabel={<FormattedMessage id="component.modal.allGood.confirm" />}
        actionBtnProps={{ primary: true }}
        handleConfirm={handleConfirm}
      >
        <FormattedMessage id="general.removeCorrectedStatusDesc" />
      </ConfirmationModal>
    );
  };

  const applyAnswerChange = (value) => {
    trackQuestionAction('Select Answer', { answer_value: value });

    if (value !== answerValues.fail) {
      setAnswer({ value, is_flagged: false, is_corrected: false, not_corrected: false });
    } else {
      setAnswer({ value, is_corrected: false, not_corrected: false });
      setNoteOpen(true);
    }
  };

  const onAnswerChange = (value) => {
    if (isCorrected && value === answer) {
      return;
    }

    if (isCorrected) {
      confirmRemoveCorrected(() => applyAnswerChange(value));
      return;
    }

    applyAnswerChange(value);
  };

  const handleMarkAsCorrected = () => {
    setAnswer({ is_corrected: true, is_flagged: false });
  };

  const applyCarryForwardDecision = (corrected) => {
    trackQuestionAction('Select Carry-Forward Decision', { decision: corrected ? 'corrected' : 'not_corrected' });

    if (corrected) {
      setAnswer({ is_corrected: true, is_flagged: false, not_corrected: false });
    } else {
      setAnswer({ is_corrected: false, not_corrected: true });
    }
  };

  const onCarryForwardDecision = (corrected) => {
    if ((corrected && isCorrected) || (!corrected && isNotCorrected)) {
      return;
    }

    if (!corrected && isCorrected) {
      confirmRemoveCorrected(() => applyCarryForwardDecision(corrected));
      return;
    }

    applyCarryForwardDecision(corrected);
  };

  const handleRemoveCorrected = () => {
    confirmRemoveCorrected(() => setAnswer({ is_corrected: false }));
  };

  const canMarkAsCorrected =
    !isDraft && !question.open_answer && !isCorrected && (answer === answerValues.fail || question.answer?.is_flagged);

  const handleSaveOpenAnswer = () => {
    trackQuestionAction('Save Open Answer');
    onAnswerChange(openAnswerText);
  };

  const renderAnswerButton = (buttonData) => (
    <div
      key={buttonData.value}
      onClick={() => onAnswerChange(buttonData.value)}
      className={cn('btn', buttonData.value, {
        selected: answer === buttonData.value,
        corrected: isCorrected && answer === buttonData.value,
      })}
    >
      <FormattedMessage id={buttonData.labelKey} />
    </div>
  );

  const carryForwardTooltip = isCarriedForward ? <FormattedMessage id="general.failedOnPreviousReport" /> : null;

  // One badge at a time: the decision (corrected / not corrected) supersedes the carried-forward mark
  const renderQuestionBadge = () => {
    if (isCorrected && !isCarryForwardDecisionMode) {
      return (
        <SVRBadge color="green" tooltip={carryForwardTooltip} onRemove={handleRemoveCorrected}>
          <FormattedMessage id="general.corrected" />
        </SVRBadge>
      );
    }
    if (isNotCorrected && !isCarryForwardDecisionMode) {
      return (
        <SVRBadge color="red" tooltip={carryForwardTooltip}>
          <FormattedMessage id="general.notCorrected" />
        </SVRBadge>
      );
    }
    if (isCarriedForward && answer === answerValues.fail) {
      return (
        <SVRBadge color="red">
          <FormattedMessage id="general.failedOnPreviousReport" />
        </SVRBadge>
      );
    }
    return null;
  };

  const questionBadge = renderQuestionBadge();

  return (
    <div id={`svr-question-${question.id}`} className={cn('question-row', { flagged: question.answer?.is_flagged })}>
      <div className={cn('header')}>
        <div className={cn('info-column')}>
          <div className={cn('question-info')}>
            <QuestionAnswerIcon
              answerValue={answer}
              questionIndex={questionIndex}
              openAnswer={question.open_answer}
            />
            <span className={cn('ml-10')}>{question.label}</span>
          </div>
          {questionBadge && <div className={cn('badge-row')}>{questionBadge}</div>}
        </div>

        <div className={cn('btn-group')}>
          {isCarryForwardDecisionMode && (
            <>
              <div
                onClick={() => onCarryForwardDecision(true)}
                className={cn('btn', 'decision-btn', 'corrected', { selected: isCorrected })}
              >
                <FormattedMessage id="general.corrected" />
              </div>
              <div
                onClick={() => onCarryForwardDecision(false)}
                className={cn('btn', 'decision-btn', 'not-corrected', { selected: isNotCorrected })}
              >
                <FormattedMessage id="general.notCorrected" />
              </div>
            </>
          )}
          {!isCarryForwardDecisionMode && !question.open_answer && answerButtons.map(renderAnswerButton)}
          {canMarkAsCorrected && (
            <div className={cn('mark-corrected-btn')} onClick={handleMarkAsCorrected}>
              <FormattedMessage id="general.markAsCorrected" />
            </div>
          )}
          <div
            onClick={toggleNote}
            className={cn('note-btn', { disabled: !answer && !question.open_answer, opened: isNoteOpen })}
          >
            {isNoteOpen ? <i className={cn('fa fa-times')} /> : <PencilIcon />}
          </div>
        </div>
      </div>

      <UnmountClosed isOpened={isNoteOpen}>
        {question.open_answer ? (
          <div className={cn('note-section')}>
            <FormattedMessage id="general.typeResponse">
              {(placeholder) => (
                <textarea
                  className="textArea"
                  placeholder={placeholder}
                  value={openAnswerText}
                  onChange={(e) => setOpenAnswerText(e.target.value)}
                />
              )}
            </FormattedMessage>

            <div className={cn('note-actions')}>
              <Button
                onClick={handleSaveOpenAnswer}
                disabled={openAnswerText === answer}
                className={cn('save-btn')}
                primary
              >
                <FormattedMessage id="general.button.save" />
              </Button>
            </div>
          </div>
        ) : (
          <>
            <QuestionComments
              categoryIndex={categoryIndex}
              questionIndex={questionIndex}
              question={question}
            />
            <QuestionNewComment
              categoryIndex={categoryIndex}
              questionIndex={questionIndex}
              setAnswer={setAnswer}
              question={question}
            />
          </>
        )}
      </UnmountClosed>
    </div>
  );
};

QuestionRow.propTypes = {
  question: T.object.isRequired,
  categoryIndex: T.number.isRequired,
  questionIndex: T.number.isRequired,
  setAnswer: T.func.isRequired,
  openModal: T.func.isRequired,
  currentUser: T.object,
  farmId: T.string,
  reportId: T.string,
  isDraft: T.bool,
};

export default connect(
  (state) => ({
    currentUser: state.auth.user,
    farmId: state.siteVisitReport.farm.id,
    reportId: state.siteVisitReport.id,
    isDraft: state.siteVisitReport.report?.isDraft,
  }), (dispatch, { questionIndex, categoryIndex  }) => ({
    setAnswer: (answer) => dispatch(setAnswer(categoryIndex, questionIndex, answer)),
    openModal: (component) => dispatch(openModal(component)),
  })
)(QuestionRow);
